import type { Influence } from '@/types/influence';

/**
 * InfluenceStorage — persists the influences library (Steal from Masters).
 *
 * Same shape as snippet-storage.ts's SnippetStorage: localStorage-only v1,
 * cloud path deferred for the same reason (shared tortoise Supabase project,
 * no new table without an infra decision — see the troth idea filed for
 * snippets' cloud sync, same reasoning applies here).
 */
class InfluenceStorage {
  private readonly LOCAL_STORAGE_KEY = 'neume-influences';

  private getFromLocalStorage(): Influence[] {
    const data = localStorage.getItem(this.LOCAL_STORAGE_KEY);
    return data ? JSON.parse(data) : [];
  }

  private writeToLocalStorage(influences: Influence[]): void {
    localStorage.setItem(this.LOCAL_STORAGE_KEY, JSON.stringify(influences));
  }

  async save(influence: Influence): Promise<void> {
    const influences = this.getFromLocalStorage();
    const index = influences.findIndex((i) => i.id === influence.id);

    if (index >= 0) {
      influences[index] = influence;
    } else {
      influences.push(influence);
    }

    this.writeToLocalStorage(influences);
  }

  async getAll(): Promise<Influence[]> {
    return this.getFromLocalStorage();
  }

  async getById(id: string): Promise<Influence | null> {
    return this.getFromLocalStorage().find((i) => i.id === id) || null;
  }

  async delete(id: string): Promise<void> {
    const influences = this.getFromLocalStorage().filter((i) => i.id !== id);
    this.writeToLocalStorage(influences);
  }
}

// Export singleton instance, mirroring snippetStorage.
export const influenceStorage = new InfluenceStorage();
